import { validationSchema, initialValues, ServiceItem,  PackageListResponse, PharmacyListResponse, getPharmacyListURL, getbillingConfigURL, billingConfigResponse, ServiceMasterItem, getServicesURL, PackageDefinitionFormValues, packageDefinitionURL, ProcedureItem, ProcedureListResponse, getProcedureLists, ProcedureServicesResponse, getDefaultServicesList, ProcedureServiceItem, packageDetailsForTariffResponse, ProcedureDetail } from './PackageDefinition.helper';
import { PackageDetailsValues, PackageTariffItem } from './PackageConfigHelper';
import React from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { TextField, RadioGroup, FormControlLabel, Radio, Checkbox, Typography, Box, Button, Grid2, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Autocomplete, IconButton, Popover, List, ListItem, ListItemText } from '@mui/material';
import { ReactComponent as DeleteIcon } from '../../../assets/SVG/delete.svg';
import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import { useAppQuery } from '../../../services/appQuery';
import { ConfirmationModal } from '../../../components/confirmationModal';
import {  useLocation, useNavigate } from 'react-router-dom';
import { useAlert } from '../../../components/alertProvider';
import { RequiredLabel } from '../../../utils/commonUtils';
import PackageDefinitionViewMode from './PackageDefinitionViewMode';
import useCurrency from '../../../components/CurrencyProvider'; 
import styles from '../../../pages/Inventory/Common/Inventory.module.css';
import { useTranslation } from 'react-i18next';

interface PackageDefinitionProps {
  onBackToStep1?: () => void;
  initialPackageData?: PackageDetailsValues;
  packageId?: number | null;
  onRefetchPackageData?: () => Promise<void>;
}

const mapDefaultServicesToServiceItems = (services: ProcedureServiceItem[], procedureId?: number): ServiceItem[] =>
  services.map(service => ({
    service: service.service_id,
    service_name: service.service_name,
    service_source_type: service.service_source_type,
    procedure: '',
    category: '',
    gender: 'both',
    adjustable_head: service.adjustable_head,
    adjustable_head_type: service.adjustable_head ? 'clinical' : '',
    is_consumables: service.is_consumables,
    discount_percent: service.discount_percent,
    discount_on_quantity: !!service.discount_on_qty,
    quantity: 1,
    unit_rate: service.unit_rate,
    unit_percentage: service.unit_percentage,
    discount_amount: service.discount_amount,
    unlimited_qty: service.unlimited_qty,
    total_amount: service.total_amount,
    original_unit_rate: service.unit_rate,
    original_quantity: 1,
    procedure_source_ids: procedureId ? [procedureId] : [],
  }));

/**
 * Merge services from multiple procedures.
 * If a service already exists (by service_id), increase quantity and add procedure to tracking.
 * Otherwise, add as new service item.
 */
const mergeServicesFromProcedures = (
  existingServices: ServiceItem[],
  newServices: ServiceItem[],
  procedureId: number
): ServiceItem[] => {
  const merged = [...existingServices];

  newServices.forEach(newService => {
    const existingIndex = merged.findIndex(s => s.service === newService.service);
    if (existingIndex >= 0) {
      // Service already exists - increase quantity and add procedure to tracking
      const existing = merged[existingIndex];
      const currentProcIds = existing.procedure_source_ids || [];
      if (!currentProcIds.includes(procedureId)) {
        const newQty = existing.quantity + 1;
        merged[existingIndex] = {
          ...existing,
          quantity: newQty,
          procedure_source_ids: [...currentProcIds, procedureId],
          // Recalculate total_amount based on new quantity using discount_amount formula
          total_amount: Number((((Number(existing.unit_rate) || 0) - (Number(existing.discount_amount) || 0)) * newQty).toFixed(2)),
        };
      }
    } else {
      // New service - add with procedure tracking
      merged.push({
        ...newService,
        procedure_source_ids: [procedureId],
      });
    }
  });

  return merged;
};

/**
 * Remove services associated with a removed procedure.
 * If a service is shared with other procedures, decrease quantity instead of removing.
 */
const removeServicesForProcedure = (
  services: ServiceItem[],
  procedureId: number
): ServiceItem[] => {
  return services
    .map(service => {
      const procIds = service.procedure_source_ids || [];
      if (!procIds.includes(procedureId)) {
        // Service not related to this procedure, keep as is
        return service;
      }
      
      if (procIds.length > 1) {
        // Service is shared with other procedures - decrease quantity
        const newProcIds = procIds.filter(id => id !== procedureId);
        const newQuantity = Number(service.quantity) - 1;
        if (newQuantity <= 0) {
          return null;
        }
        return {
          ...service,
          quantity: newQuantity,
          procedure_source_ids: newProcIds,
          total_amount: Number((((Number(service.unit_rate) || 0) - (Number(service.discount_amount) || 0)) * newQuantity).toFixed(2)),
        };
      }
      
      // Service only belongs to this procedure - mark for removal
      return null;
    })
    .filter((s): s is ServiceItem => s !== null);
};

const DefaultServicesApplier: React.FC<{ 
  defaultServices?: ProcedureServicesResponse; 
  procedureId?: number;
  onServicesLoaded?: (procedureId: number, services: ServiceItem[]) => void;
}> = ({ defaultServices, procedureId, onServicesLoaded }) => {
  // Track which procedure ID we last processed to prevent duplicate processing
  const lastProcessedProcedureId = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Need a valid procedureId to process
    if (!procedureId) return;
    
    // Skip if we already processed this exact procedure ID
    if (lastProcessedProcedureId.current === procedureId) return;
    
    // Wait for defaultServices to be loaded (not undefined)
    if (defaultServices === undefined) return;
    
    // If API returns procedure_id, ensure it matches - prevents stale data issues
    if (defaultServices.procedure_id && defaultServices.procedure_id !== procedureId) return;
    
    // Mark this procedure as processed
    lastProcessedProcedureId.current = procedureId;
    
    if (!defaultServices.services || defaultServices.services.length === 0) {
      // No services for this procedure, but still mark as loaded
      onServicesLoaded?.(procedureId, []);
      return;
    }
    
    const newServices = mapDefaultServicesToServiceItems(defaultServices.services, procedureId);
    onServicesLoaded?.(procedureId, newServices);
  }, [defaultServices, procedureId, onServicesLoaded]);

  // Reset processed tracking when procedureId changes (allows re-processing if needed)
  React.useEffect(() => {
    if (procedureId === undefined) {
      lastProcessedProcedureId.current = null;
    }
  }, [procedureId]);

  return null;
};


// Sub-component for each tariff's form
interface PackageDefinitionFormProps {
  packageId: number;
  tariffId: number;
  tariffName: string;
  onBackToStep1?: () => void;
  packageDetailsValues?: PackageDetailsValues | null;
  import_tariff_id?: number;
  setImportTariffId?: (id: number | undefined) => void;
  onRefetchInitialDetails?: () => void;
  allTariffNames?: string[];
  definedTariffNames?: string[];
}

type PackageDefinitionSubmitPayload = Omit<PackageDefinitionFormValues, 'pharmacy_items'> & {
  service_items: PackageDefinitionFormValues['service_items'];
  pharmacy_items: PackageDefinitionFormValues['pharmacy_items'];
};

const PackageDefinitionForm: React.FC<PackageDefinitionFormProps> = ({ 
  packageId, 
  tariffId, 
  tariffName,
  onBackToStep1,
  packageDetailsValues,
  import_tariff_id,
  setImportTariffId,
  onRefetchInitialDetails,
  allTariffNames,
  definedTariffNames,
}) => {
  const location = useLocation();
  const packageDetailFromState = (location?.state as { packageDetail?: PackageListResponse['objects'][0] } | null)?.packageDetail;
  const { addAlert }  = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  // Initialize isViewMode based on whether we have existing package data and not importing
  // This prevents flash of edit form before view form loads
  const [isViewMode, setIsViewMode] = React.useState(!import_tariff_id && Boolean(packageId));
  const navigate = useNavigate();
  const lastHandledIdRef = React.useRef<string | null>(null);
  const isImportActiveRef = React.useRef(!!import_tariff_id);
  const [submitPayload, setSubmitPayload] = React.useState<PackageDefinitionSubmitPayload | null>(null);
  const submissionId = submitPayload ? JSON.stringify(submitPayload) : '';
  const [activeTab, setActiveTab] = React.useState<'services' | 'pharmacy'>('services');
  const [inputValue, setInputValue] = React.useState('');
  const [inputValueForService, setInputValueForService] = React.useState('');
  const [pharmacySearch, setPharmacySearch] = React.useState('');
  const [serviceSearch, setServiceSearch] = React.useState('');
  const [procedureSearch, setProcedureSearch] = React.useState('');
  const [selectedItemGroupCode, setSelectedItemGroupCode] = React.useState<string>('');
  const [selectedItemCategoryCode, setSelectedItemCategoryCode] = React.useState<string>('');
  const [selectedDrug, setSelectedDrug] = React.useState<PharmacyItemType | null>(null);
  const [selectedService, setSelectedService] = React.useState<ServiceMasterItem | null>(null);
  // Support multi-select for cycles/procedures - using array for backward compatibility
  const [selectedProcedures, setSelectedProcedures] = React.useState<ProcedureItem[]>([]);
  const [isPatchedProcedure, setIsPatchedProcedure] = React.useState(false);
  // Track which procedures have had their default services loaded
  const [loadedProcedureIds, setLoadedProcedureIds] = React.useState<Set<number>>(new Set());
  // Track existing procedure_details from API for PUT operations (preserves `id` for update/delete)
  const [existingProcedureDetails, setExistingProcedureDetails] = React.useState<ProcedureDetail[]>([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [openFinalizeModal, setOpenFinalizeModal] = React.useState(false);
  
  // Store pharmacy items before clearing when Set All Items is checked
  const [savedPharmacyItems, setSavedPharmacyItems] = React.useState<PackageDefinitionFormValues['pharmacy_items'] | null>(null);

  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [pendingDeleteAction, setPendingDeleteAction] = React.useState<(() => void) | null>(null);

  // Popover state for "Service From No. of Cycles" badge
  const [cyclePopoverAnchor, setCyclePopoverAnchor] = React.useState<{ element: HTMLElement | null; serviceIdx: number | null }>({
    element: null,
    serviceIdx: null,
  });

  const handleCyclePopoverOpen = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    setCyclePopoverAnchor({ element: event.currentTarget, serviceIdx });
  };

  const handleCyclePopoverClose = () => {
    setCyclePopoverAnchor({ element: null, serviceIdx: null });
  };

  const handleCyclePopoverKeyDown = (event: React.KeyboardEvent<HTMLElement>, serviceIdx: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCyclePopoverOpen(event, serviceIdx);
    }
  };

  // Get procedure names from IDs for popover display
  const getProcedureNamesFromIds = React.useCallback((procedureIds: number[]): string[] => {
    if (!procedureIds || procedureIds.length === 0) return [];
    return procedureIds
      .map(id => {
        const proc = selectedProcedures.find(p => p.id === id);
        return proc?.name_label || proc?.name || '';
      })
      .filter(Boolean);
  }, [selectedProcedures]);

  const handleDeleteClick = (deleteAction: () => void) => {
    setPendingDeleteAction(() => deleteAction);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteAction) {
      pendingDeleteAction();
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteAction(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteAction(null);
  };

  const { currencySymbol } = useCurrency();

  type PharmacyItemType = PharmacyListResponse['objects'][0];

  // Get the latest selected procedure that hasn't had its services loaded yet
  const latestUnloadedProcedure = React.useMemo(() => {
    return selectedProcedures.find(p => !loadedProcedureIds.has(p.id)) || null;
  }, [selectedProcedures, loadedProcedureIds]);

  const shouldFetchDefaultServices = !!latestUnloadedProcedure?.id;

  // Find matching tariff to get the correct tariff_id for API
  const matchedTariff = packageDetailsValues?.tariffs?.find(t => t.id === tariffId);
  const tariffIdForApi = matchedTariff?.tariff ;

  const { data: DefaultServices } = useAppQuery<ProcedureServicesResponse>(
    ['defaultServices', latestUnloadedProcedure?.id, tariffIdForApi],
    () => getRequest<ProcedureServicesResponse>(
      getDefaultServicesList(latestUnloadedProcedure?.id, latestUnloadedProcedure?.type, tariffIdForApi)
    ),
    {
      staleTime: 0,
      gcTime: 0,
      enabled: shouldFetchDefaultServices,
    }

  );


  // Update ref when import_tariff_id changes from parent
  React.useEffect(() => {
    isImportActiveRef.current = !!import_tariff_id;
  }, [import_tariff_id]);
  
  const { data: packageDetails, refetch: refetchPackageDetails, isFetched: isPackageDetailsFetched, isError: isPackageDetailsError } = useAppQuery<PackageDefinitionFormValues>(
    ['packageDefinitionDetails', packageId, tariffId, import_tariff_id],
    () => {
      // Only use import logic if ref is active
      if (isImportActiveRef.current && import_tariff_id) {
        const baseUrl = packageDefinitionURL(packageId, import_tariff_id);
        const url = `${baseUrl}?import_tariff=${tariffId}`;
        return getRequest<PackageDefinitionFormValues>(url);
      }
      return getRequest<PackageDefinitionFormValues>(packageDefinitionURL(packageId, tariffId));
    },
    { staleTime: 0, 
      gcTime: 0,
      enabled: typeof packageId === 'number' && typeof (import_tariff_id || tariffId) === 'number',
      retry: false,
    }
  );

  const { data: ProcedureLists } = useAppQuery<ProcedureListResponse>(
    ['procedureLists', procedureSearch],
    () => getRequest<ProcedureListResponse>(
      getProcedureLists() + (procedureSearch ? `?search=${encodeURIComponent(procedureSearch)}` : '')
    ),
    { staleTime: 0, gcTime:0, debounceMs: 300 }
  );

  // Memoize formInitialValues to avoid recalculating on every render
  const formInitialValues = React.useMemo(() => {
    if (packageDetails?.package_definition) {
      const isPharmacySetAllItems = packageDetails.package_definition.pharmacy_set_all_items || false;
      const pharmacyItemsFromApi = packageDetails.pharmacy_items || [];
      
      // When pharmacy_set_all_items is true, ensure a blank disabled row exists
      const pharmacyItems = isPharmacySetAllItems && pharmacyItemsFromApi.length === 0
        ? [{
          item: 0,
          item_name: '',
          quantity: 0,
          uom: null,
          discount_percent: 0,
          uom_dropdown: null,
        }]
        : pharmacyItemsFromApi.map(item => ({
          ...item,
          uom_dropdown: item.uom_conversion || item.uom_dropdown || null,
        }));

      // Get all procedure IDs from procedure_details for populating service_items
      const allProcedureIds = ((packageDetails.package_definition as unknown as { procedure_details?: ProcedureDetail[] }).procedure_details || 
        (packageDetails as unknown as { procedure_details?: ProcedureDetail[] }).procedure_details || [])
        .map(pd => pd.procedure_id)
        .filter((id): id is number => typeof id === 'number');

      // Map service_items to ensure procedure_source_ids is populated
      // Extract from service item's own procedure_details array (from GET API response)
      const serviceItemsWithProcedureIds = (packageDetails.service_items || []).map(item => {
        // Check if service item has its own procedure_details array from API
        const itemProcedureDetails = (item as unknown as { procedure_details?: Array<{ procedure_id: number }> }).procedure_details;
        
        // Extract procedure_source_ids from the service item's procedure_details
        const extractedProcIds = itemProcedureDetails && itemProcedureDetails.length > 0
          ? itemProcedureDetails.map(pd => pd.procedure_id).filter((id): id is number => typeof id === 'number')
          : [];
        
        return {
          ...item,
          // Priority: service item's procedure_details > existing procedure_source_ids > fallback to all procedure IDs
          procedure_source_ids: extractedProcIds.length > 0
            ? extractedProcIds
            : (item.procedure_source_ids && item.procedure_source_ids.length > 0 
              ? item.procedure_source_ids 
              : allProcedureIds),
        };
      });

      // Normalize procedure_details into package_definition (API may return at either level)
      const procedureDetailsForFormik = 
        (packageDetails.package_definition as unknown as { procedure_details?: ProcedureDetail[] }).procedure_details ||
        (packageDetails as unknown as { procedure_details?: ProcedureDetail[] }).procedure_details ||
        [];

      return {
        ...packageDetails,
        package_definition: {
          ...packageDetails.package_definition,
          service_percent: Number(packageDetails.package_definition.service_pct ?? packageDetails.package_definition.service_percent ?? 0),
          pharmacy_percent: Number(packageDetails.package_definition.pharmacy_pct ?? packageDetails.package_definition.pharmacy_percent ?? 0),
          procedure_details: procedureDetailsForFormik,
        },
        service_items: serviceItemsWithProcedureIds,
        pharmacy_items: pharmacyItems,
      };
    } else if (packageDetailsValues) {
      return {
        ...initialValues,
        package_definition: {
          ...initialValues.package_definition,
          package_name: packageDetailsValues.package_name || '',
          package_amount: Number(packageDetailsValues.package_amount || 0),
        },
      };
    } else if (packageDetailFromState) {
      return {
        ...initialValues,
        package_definition: {
          ...initialValues.package_definition,
          package_amount: Number(packageDetailFromState?.package_amount || 0),
        },
      };
    }
    return initialValues;
  }, [packageDetails, packageDetailsValues, packageDetailFromState]);


  React.useEffect(() => {
    if (packageDetails?.package_definition && !import_tariff_id) {
      setIsViewMode(true);
    } else if (packageDetails && !packageDetails.package_definition) {
      // No definition exists yet for this tariff, show edit mode
      setIsViewMode(false);
    } else if (isPackageDetailsFetched && !packageDetails) {
      // No data exists (404 or empty response), switch to edit mode
      setIsViewMode(false);
    } else if (isPackageDetailsError) {
      // Error fetching data, switch to edit mode
      setIsViewMode(false);
    }
  }, [packageDetails, import_tariff_id, isPackageDetailsFetched, isPackageDetailsError]);

  // When editing an existing package, preselect the procedure(s) in the autocomplete
  React.useEffect(() => {
    if (!packageDetails?.package_definition || isPatchedProcedure) return;

    // Check both package_definition level and root level for procedure_details (API may return at either level)
    const anyDetails = packageDetails.package_definition as unknown as {
      procedure_source_type?: string;
      procedure_source_id?: number | null;
      procedure_details?: ProcedureDetail[];
    };
    const rootDetails = packageDetails as unknown as {
      procedure_details?: ProcedureDetail[];
    };

    // Support new procedure_details array (check both root and nested) or fallback to single ID (backward compat)
    const procedureDetails: ProcedureDetail[] = 
      anyDetails.procedure_details || 
      rootDetails.procedure_details ||
      (anyDetails.procedure_source_id ? [{
        procedure_id: anyDetails.procedure_source_id,
        procedure_source_type: anyDetails.procedure_source_type,
      }] : []);

    if (procedureDetails.length === 0) return;

    // Store existing procedure details for PUT operations (preserves `id` for update/delete)
    setExistingProcedureDetails(procedureDetails);

    // Create ProcedureItem objects directly from procedure_details without needing to look up in ProcedureLists
    // The GET API returns all needed info: procedure_id, procedure_source_type, procedure_source_name
    const matchedProcedures: ProcedureItem[] = procedureDetails
      .filter(detail => detail.procedure_id)
      .map(detail => ({
        id: detail.procedure_id,
        type: detail.procedure_source_type || 'procedure',
        name: detail.procedure_source_name || '',
        name_label: detail.procedure_source_name || '',
        // Fill in required fields with defaults
        description: '',
        icon_path: '',
        gender: '',
        procedure_type: '',
        for_patient_type: '',
        overview_links: '',
        pages: '',
        description_label: '',
      }));

    // Always mark as patched to prevent re-running
    setIsPatchedProcedure(true);

    if (matchedProcedures.length > 0) {
      setSelectedProcedures(matchedProcedures);
      // Mark all matched procedures as already loaded to prevent refetching default services
      setLoadedProcedureIds(new Set(matchedProcedures.map(p => p.id)));
    }
  }, [packageDetails, isPatchedProcedure]);


  const pharmacyQueryParams = React.useMemo(() => [
    pharmacySearch ? `search=${encodeURIComponent(pharmacySearch)}` : '',
    selectedItemGroupCode ? `item_group=${encodeURIComponent(selectedItemGroupCode)}` : '',
    selectedItemCategoryCode ? `item_category=${encodeURIComponent(selectedItemCategoryCode)}` : '',
  ].filter(Boolean).join('&'), [
    pharmacySearch,
    selectedItemGroupCode,
    selectedItemCategoryCode,
  ]);

  const { data: pharmacyList, refetch: refetchPharmacyList } = useAppQuery<PharmacyListResponse>(
    ['pharmacyList', pharmacySearch, selectedItemGroupCode, selectedItemCategoryCode],
    () => getRequest<PharmacyListResponse>(
      getPharmacyListURL() + (pharmacyQueryParams ? `&${pharmacyQueryParams}` : '')
    ),
    { 
      staleTime: 5 * 60 * 1000,
      debounceMs: 300
    }
  );

  const { data: billingConfig } = useAppQuery<billingConfigResponse>(
    ['billingConfig'],
    () => getRequest<billingConfigResponse>(getbillingConfigURL()),
    { staleTime: 5 * 60 * 1000 }
  );
  
  


  const shouldFetchServices = !!serviceSearch;
  const { data: servicesList } = useAppQuery<ServiceMasterItem[]>(
    ['packageServicesList', serviceSearch, tariffIdForApi],
    () => getRequest<ServiceMasterItem[]>(
      getServicesURL() +
      (serviceSearch
        ? `?search_query=${encodeURIComponent(serviceSearch)}`
        : '') +
      (tariffIdForApi
        ? `${serviceSearch ? '&' : '?'}tariff_id=${encodeURIComponent(String(tariffIdForApi))}`
        : '')
    ),
    {
      staleTime: 5 * 60 * 1000,
      enabled: shouldFetchServices,
      debounceMs: 300
    }
  );

  
  const {
    isError: postIsError,
    error: postError,
    isSuccess: postIsSuccess,
  } = useAppQuery(
    ['submitPackageDefinition', submitPayload, packageDetails],
    () => {
       
      if (!submitPayload) return Promise.resolve(null);
      const updateId = packageDetails?.package_definition?.id;
      const createPkgId = packageId || 0;
      if (updateId) {
        return putRequest(packageDefinitionURL(createPkgId, tariffId), submitPayload);
      }
      return postRequest(packageDefinitionURL(createPkgId, tariffId), submitPayload);
    },
    {
      enabled: !!submitPayload,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );


  React.useEffect(() => {
    const handlePostSuccess = async () => {
      if (submitPayload && lastHandledIdRef.current !== submissionId) {
        if (postIsSuccess) {
          addAlert(tBilling('package_config.messages.package_definition_saved_for_tariff', { defaultValue: 'Package definition saved successfully for {{tariffName}}.', tariffName }), 'success');
          isImportActiveRef.current = false; // Clear before refetch
          setImportTariffId?.(undefined);
          lastHandledIdRef.current = submissionId;
          setSubmitPayload(null);
          await refetchPackageDetails();
          await onRefetchInitialDetails?.(); // Refetch initial package details for tariff counts
          setIsViewMode(true); // Go to view mode after successful save
        } else if (postIsError) {
          addAlert(tBilling('package_config.messages.failed_to_save_package_details', { defaultValue: 'Failed to save package details.' }), 'error');
          // eslint-disable-next-line no-console
          console.error('Post package details error:', postError);
          lastHandledIdRef.current = submissionId;
          setSubmitPayload(null);
        }
      }
    };
    handlePostSuccess();
  }, [postIsSuccess, postIsError, postError, submitPayload, submissionId, addAlert, refetchPackageDetails, tariffName, setImportTariffId, onRefetchInitialDetails, tBilling]);

  function buildPayload(values: PackageDefinitionFormValues): PackageDefinitionSubmitPayload {
    const pharmacySetAllItems = values.package_definition?.pharmacy_set_all_items || false;
    const hasValidServiceItems = (values.service_items || []).some(
      item => Number(item?.service) > 0 || Boolean(item?.service_name)
    );
    const hasValidPharmacyItems = (values.pharmacy_items || []).some(
      item => Number(item?.item) > 0 || Boolean(item?.item_name)
    );

    return {
      package_definition: {
        ...packageDetails?.package_definition ? { id: packageDetails.package_definition.id } : {},
        duration_type: values.package_definition.duration_type,
        distribution_type: values.package_definition.distribution_type,
        package_duration: values.package_definition.package_duration,
        pharmacy_amount: values.package_definition.pharmacy_amount,
        package_amount: values.package_definition.package_amount,
        service_amount: values.package_definition.service_amount,
        service_pct: values.package_definition.service_percent,
        pharmacy_pct: values.package_definition.pharmacy_percent,
        package_name: values.package_definition.package_name,
        total_service_amount: values.package_definition.total_service_amount,
        remark: values.package_definition.remark,
        is_finalized: values.package_definition.is_finalized,
        // Build procedure_details array for multi-select support
        procedure_details: selectedProcedures.map(p => {
          // If editing, find existing procedure detail to preserve `id` for PUT update
          const existingDetail = existingProcedureDetails.find(ed => ed.procedure_id === p.id);
          return {
            ...(existingDetail?.id ? { id: existingDetail.id } : {}),
            procedure_id: p.id,
          };
        }),
        adjustable_head: values.package_definition.adjustable_head || false,
        is_consumables: values.package_definition.is_consumables || false,
        consumable_amount: values.package_definition.consumable_amount || 0,
        pharmacy_set_all_items: pharmacySetAllItems
      },
      service_items: hasValidServiceItems
        ? (values.service_items || []).map(item => ({
          ...(item.id ? { id: item.id } : {}),
          service: item.service,
          service_name: item.service_name,
          service_source_type: item.service_source_type,
          procedure: item.procedure,
          category: item.category,
          gender: item.gender,
          adjustable_head: item.adjustable_head,
          ...(item.adjustable_head ? { adjustable_head_type: item.adjustable_head_type } : {}),
          is_consumables: item.is_consumables,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          discount_on_quantity: item.discount_on_quantity,
          quantity: item.quantity,
          unit_rate: item.unit_rate,
          unlimited_qty: item.unlimited_qty,
          unit_percentage: item.unit_percentage,
          total_amount: item.total_amount,
          // Send procedure_details for each service based on which cycles it belongs to
          procedure_details: (item.procedure_source_ids || []).map(procId => {
            // For PUT requests, find existing procedure detail to preserve `id`
            const existingDetail = existingProcedureDetails.find(ed => ed.procedure_id === procId);
            return {
              ...(existingDetail?.id ? { id: existingDetail.id } : {}),
              procedure_id: procId,
            };
          }),
        }))
        : [],
      pharmacy_items: pharmacySetAllItems
        ? []
        : hasValidPharmacyItems
          ? (values.pharmacy_items || []).map(item => ({
            ...(item.id ? { id: item.id } : {}),
            item: item.item,
            item_name: item.item_name,
            uom: item.uom,
            quantity: item.quantity,
            discount_percent: item.discount_percent,
            total_amount: item.total_amount,
          }))
          : [],
    };
  }

  const handleSubmit = (values: PackageDefinitionFormValues) => {
    const payload = buildPayload(values);

    setSubmitPayload(payload);
  };

  // Wrap Formik submit to show confirmaton modal
  const handleOpenModal = () => {
    setOpenModal(true);
  };

  // Actual submit after confirmation
  const handleConfirmedSubmit = (values: PackageDefinitionFormValues) => {
    if (values) {
      handleSubmit(values);
      setOpenModal(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };


  if (isViewMode) {
    if (!packageDetails) return null;
    return (
      <PackageDefinitionViewMode
        data={packageDetails}
        onBack={() => setIsViewMode(false)}
      />
    );
  }
  return (
    <>
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleOpenModal}
      >
        {({ values, setFieldValue, handleChange, handleBlur, errors, touched, submitForm, validateForm, setTouched }) => {
          const packageAmount = values.package_definition?.package_amount || 0;
          const pkgAmt = Number(packageAmount) || 0;
          const pharmAmt = Number(values.package_definition.pharmacy_amount) || 0;
          const servAmt = Number(values.package_definition.service_amount) || 0;
          const pharmPercent = pkgAmt ? ((pharmAmt / pkgAmt) * 100).toFixed(2) : '0.00';
          const servPercent = pkgAmt ? ((servAmt / pkgAmt) * 100).toFixed(2) : '0.00';

          // --- Custom validation summary flags ---
          const totalServicesAmount = (values.service_items || []).reduce((sum, si) => {
            const rowTotal = Number(si.total_amount) || 0;
            return sum + rowTotal;
          }, 0);

          const serviceAmountNumeric = Number(values.package_definition.service_amount || 0);
          const pharmacyAmountNumeric = Number(values.package_definition.pharmacy_amount || 0);

          // SERVICE VALIDATION:
          // Skip service amount matching validation only when BOTH amounts are 0
          const shouldSkipServiceAmountValidation = serviceAmountNumeric === 0 && totalServicesAmount === 0;
          const isServiceAmountMatch = !shouldSkipServiceAmountValidation &&
            Number(serviceAmountNumeric.toFixed(2)) === Number(totalServicesAmount.toFixed(2));

          // Service items validation: ONLY required when service_amount > 0
          const hasAtLeastOneService = (values.service_items || []).some(
            si => si.service || si.service_name
          );
          const serviceItemsRequired = serviceAmountNumeric > 0; // Only require items if amount > 0
          const isServiceValid = !serviceItemsRequired || hasAtLeastOneService; // If amount is 0, skip items check

          // PHARMACY VALIDATION:
          // Pharmacy validation only applies when pharmacy_amount > 0
          const pharmacySetAllItems = values.package_definition?.pharmacy_set_all_items || false;
          const hasAtLeastOnePharmacy = pharmacySetAllItems || (values.pharmacy_items || []).some(
            pi => pi.item || pi.item_name
          );
          const pharmacyItemsRequired = pharmacyAmountNumeric > 0; // Only require items if amount > 0
          const isPharmacyValid = !pharmacyItemsRequired || hasAtLeastOnePharmacy; // If amount is 0, skip items check

          // OVERALL VALIDATION:
          // Service amount validation (or skip if both amounts are 0)
          const isServiceAmountValid = shouldSkipServiceAmountValidation || isServiceAmountMatch;
          // Both service and pharmacy must be valid (considering their amount thresholds)
          const allCustomValid = isServiceAmountValid && isServiceValid && isPharmacyValid;

          const validationMessages: Array<{ text: string; color: string; weight: number }> = [];
          if (serviceAmountNumeric > 0) {
            validationMessages.push({
              text: tBilling('package_config.validation.service_amount_should_match_total', { defaultValue: 'Service amount should exactly match with Total Services Amount' }),
              color: shouldSkipServiceAmountValidation ? 'text.secondary' : (isServiceAmountMatch ? 'success.main' : 'error.main'),
              weight: shouldSkipServiceAmountValidation ? 400 : (isServiceAmountMatch ? 600 : 500),
            });
          }
          if (serviceItemsRequired) {
            validationMessages.push({
              text: tBilling('package_config.validation.at_least_one_service', { defaultValue: 'At least one Service should be added' }),
              color: isServiceValid ? 'success.main' : 'error.main',
              weight: isServiceValid ? 600 : 500,
            });
          }
          if (pharmacyItemsRequired) {
            validationMessages.push({
              text: tBilling('package_config.validation.at_least_one_pharmacy', { defaultValue: 'At least one Pharmacy item should be added' }),
              color: isPharmacyValid ? 'success.main' : 'error.main',
              weight: isPharmacyValid ? 600 : 500,
            });
          }
          if (!serviceItemsRequired && !pharmacyItemsRequired) {
            validationMessages.push({
              text: tBilling('package_config.validation.no_items_required', { defaultValue: 'Package amounts are 0, so item rows are not required' }),
              color: 'success.main',
              weight: 600,
            });
          }

          const isAmount = values.package_definition.distribution_type === 'amount';
          // For percent mode, use percent fields for input, calculate amount for display
          const pharmacyDisplay = isAmount
            ? values.package_definition.pharmacy_amount
            : values.package_definition.pharmacy_percent;
          const serviceDisplay = isAmount
            ? values.package_definition.service_amount
            : values.package_definition.service_percent;
          // Check if RATE_EDIT rule is active from packageDetailsValues
          const rateEditRule = packageDetailsValues?.rules?.find(r => r.rule_code === 'RATE_EDIT');
          const isRateEditable = Boolean(rateEditRule?.active);
          // Rate is editable only when there's existing data (editing) and RATE_EDIT rule is active
          const canEditPackageAmount = isRateEditable;
          
          // Min/Max validation for Package Amount from RATE_EDIT rule
          const rateEditMinAmount = rateEditRule?.min_amount ? Number(rateEditRule.min_amount) : null;
          const rateEditMaxAmount = rateEditRule?.max_amount ? Number(rateEditRule.max_amount) : null;

          // Check CONCESSION rule for pharmacy item discount validation
          const concessionRule = packageDetailsValues?.rules?.find(r => r.rule_code === 'CONCESSION' && r.active);
          const concessionMinPercent = concessionRule ? Number(concessionRule.min_percent) || 0 : 0;
          const concessionMaxPercent = concessionRule ? Number(concessionRule.max_percent) || 100 : 100;
          const currentPackageAmount = Number(values.package_definition.package_amount) || 0;
          
          let packageAmountError = '';
          if (canEditPackageAmount && currentPackageAmount > 0) {
            if (rateEditMinAmount !== null && currentPackageAmount < rateEditMinAmount) {
              packageAmountError = tBilling('package_config.validation.amount_min', { defaultValue: 'Amount must be at least {{currencySymbol}}{{min}}', currencySymbol, min: rateEditMinAmount });
            } else if (rateEditMaxAmount !== null && currentPackageAmount > rateEditMaxAmount) {
              packageAmountError = tBilling('package_config.validation.amount_max', { defaultValue: 'Amount must not exceed {{currencySymbol}}{{max}}', currencySymbol, max: rateEditMaxAmount });
            }
          }

          // Calculate unfilled tariffs for finalize validation
          // Check if current form has meaningful data (package_amount > 0 or items exist)
          const currentFormHasData = currentPackageAmount > 0 || 
            (values.service_items && values.service_items.length > 0) || 
            (values.pharmacy_items && values.pharmacy_items.length > 0);
          
          // Calculate which tariffs are filled
          const filledTariffNames = new Set(definedTariffNames || []);
          if (currentFormHasData && tariffName) {
            filledTariffNames.add(tariffName);
          }
          
          // Calculate unfilled tariffs
          const unfilledTariffNames = (allTariffNames || []).filter(name => !filledTariffNames.has(name));
          const unfilledCount = unfilledTariffNames.length;
          
          // Build dynamic message for finalize modal
          let finalizeMessage = tBilling('package_config.messages.confirm_save_finalize_package', { defaultValue: 'Do you want to save and finalize the package?' });
          if (unfilledCount > 0) {
            const tariffList = unfilledTariffNames.join(', ');
            finalizeMessage = tBilling('package_config.messages.unfilled_tariffs_finalize', { defaultValue: '{{count}} tariff(s) not filled ({{tariffList}}). Do you still want to finalize?', count: unfilledCount, tariffList });
          }

          return (
            <Box sx={{ p: 3 }}>
              <DefaultServicesApplier 
                defaultServices={DefaultServices} 
                procedureId={latestUnloadedProcedure?.id}
                onServicesLoaded={(procId, newServices) => {
                  // Mark procedure as loaded
                  setLoadedProcedureIds(prev => {
                    const newSet = new Set(Array.from(prev));
                    newSet.add(procId);
                    return newSet;
                  });
                  
                  // If there are services to add, merge them with existing
                  if (newServices.length > 0) {
                    const currentServices = values.service_items || [];
                    const validCurrentServices = currentServices.filter(s => s.service && s.service_name);
                    const mergedServices = mergeServicesFromProcedures(validCurrentServices, newServices, procId);
                    setFieldValue('service_items', mergedServices);
                    
                    // Recalculate total
                    const totalAmount = mergedServices.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
                    setFieldValue('package_definition.total_service_amount', totalAmount);
                  }
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{tBilling('package_config.package_definition', { defaultValue: 'Package Definition' })}</Typography>
              <Form autoComplete="off" data-testid="package-definition-form">
                <Box>
                  <Grid2 container spacing={2}>

                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="pkgdef-name-input"
                        fullWidth
                        size="small"
                        label={RequiredLabel(tBilling('package_config.headers.package_name', { defaultValue: 'Package Name' }), true)}
                        disabled
                        sx={{
                          backgroundColor: '#FAFAFA',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        }}
                        name="package_definition.package_name"
                        value={values.package_definition.package_name || ''}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="pkgdef-amount-input"
                        fullWidth
                        size="small"
                        label={tBilling('package_config.headers.package_amount', { defaultValue: 'Package Amount' })}
                        type="number"
                        disabled={values.package_definition.is_finalized || !canEditPackageAmount}
                        sx={(values.package_definition.is_finalized || !canEditPackageAmount) ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        name="package_definition.package_amount"
                        value={values.package_definition.package_amount}
                        onChange={(e) => {
                          handleChange(e);
                          // Reset service and pharmacy amounts when package amount changes
                          setFieldValue('package_definition.service_amount', 0);
                          setFieldValue('package_definition.pharmacy_amount', 0);
                          setFieldValue('package_definition.service_percent', 0);
                          setFieldValue('package_definition.pharmacy_percent', 0);
                        }}
                        onBlur={handleBlur}
                        error={Boolean(packageAmountError)}
                        helperText={packageAmountError || (canEditPackageAmount && rateEditMinAmount !== null && rateEditMaxAmount !== null ? tBilling('package_config.validation.amount_range', { defaultValue: 'Range: {{currencySymbol}}{{min}} - {{currencySymbol}}{{max}}', currencySymbol, min: rateEditMinAmount, max: rateEditMaxAmount }) : '')}
                        slotProps={{ 
                          input: { readOnly: !canEditPackageAmount }, 
                          inputLabel: { shrink: true } 
                        }}
                      />
                      
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="pkgdef-duration-input"
                        fullWidth
                        size="small"
                        label={RequiredLabel(tBilling('package_config.package_duration', { defaultValue: 'Package Duration' }), true)}
                        disabled={values.package_definition.is_finalized}
                        sx={values.package_definition.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        name="package_definition.package_duration"
                        value={values.package_definition.package_duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.package_definition?.package_duration && Boolean(errors.package_definition?.package_duration)}
                      />
                    </Grid2>

                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <Autocomplete
                        data-testid="pkgdef-duration-type-select"
                        options={billingConfig?.DURATION_TYPE ?? []}
                        getOptionLabel={option => String(option.name)}
                        disabled={values.package_definition.is_finalized}
                        sx={values.package_definition.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        value={billingConfig?.DURATION_TYPE?.find(opt => opt.code === values.package_definition.duration_type) || null}
                        onChange={(_, newValue) => setFieldValue('package_definition.duration_type', newValue ? newValue.code : '')}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={RequiredLabel(tBilling('package_config.duration_type', { defaultValue: 'Duration Type' }), true)}
                            size="small"
                            error={touched.package_definition?.duration_type && Boolean(errors.package_definition?.duration_type)}
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                      />
                    </Grid2>

                  </Grid2>
                  <Grid2 container spacing={2} sx={{ mt: 2 }}>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Typography sx={{ fontWeight: 500, mr: 2, whiteSpace: 'nowrap' }}>{tBilling('package_config.distribution_details', { defaultValue: 'Distribution Details' })}</Typography>
                        <RadioGroup
                          row
                          name="package_definition.distribution_type"
                          value={values.package_definition.distribution_type}
                          onChange={(e) => {
                            const newDistributionType = e.target.value;
                            handleChange(e);
                            // Recalculate values when switching distribution types only if target fields are empty
                            if (newDistributionType === 'amount' && pkgAmt > 0) {
                              // Switching to amount mode: only recalculate if amounts are 0 and percentages have values
                              const currentServicePercent = Number(values.package_definition.service_percent) || 0;
                              const currentPharmacyPercent = Number(values.package_definition.pharmacy_percent) || 0;
                              if (servAmt === 0 && currentServicePercent > 0) {
                                const newServiceAmt = (pkgAmt * currentServicePercent) / 100;
                                setFieldValue('package_definition.service_amount', Number(newServiceAmt.toFixed(2)));
                              }
                              if (pharmAmt === 0 && currentPharmacyPercent > 0) {
                                const newPharmacyAmt = (pkgAmt * currentPharmacyPercent) / 100;
                                setFieldValue('package_definition.pharmacy_amount', Number(newPharmacyAmt.toFixed(2)));
                              }
                            } else if (newDistributionType === 'percentage' && pkgAmt > 0) {
                              // Switching to percentage mode: only recalculate if percentages are 0 and amounts have values
                              const currentServicePercent = Number(values.package_definition.service_percent) || 0;
                              const currentPharmacyPercent = Number(values.package_definition.pharmacy_percent) || 0;
                              if (currentServicePercent === 0 && servAmt > 0) {
                                const newServicePercent = (servAmt / pkgAmt) * 100;
                                setFieldValue('package_definition.service_percent', Number(newServicePercent.toFixed(2)));
                              }
                              if (currentPharmacyPercent === 0 && pharmAmt > 0) {
                                const newPharmacyPercent = (pharmAmt / pkgAmt) * 100;
                                setFieldValue('package_definition.pharmacy_percent', Number(newPharmacyPercent.toFixed(2)));
                              }
                            }
                          }}

                          sx={{ ml: 1 }}
                        >
                          <FormControlLabel value="amount"  disabled={values.package_definition.is_finalized}
                            sx={values.package_definition.is_finalized ? {
                              backgroundColor: '#fafafa',
                              opacity: 0.9,
                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                            } : undefined} control={<Radio color="error" />} label={tBilling('package_details.amount', { defaultValue: 'Amount' })} />
                          <FormControlLabel value="percentage" disabled={values.package_definition.is_finalized}
                            sx={values.package_definition.is_finalized ? {
                              backgroundColor: '#fafafa',
                              opacity: 0.9,
                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                            } : undefined} control={<Radio color="error" />} label={tBilling('package_config.percentage', { defaultValue: 'Percentage(%)' })} />
                        </RadioGroup>
                      </Box>
                    </Grid2>
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="pkgdef-service-distribution-input"
                        fullWidth
                        size="small"
                        disabled={ !values.package_definition.distribution_type  || values.package_definition.is_finalized}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={values.package_definition.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        label={
                          isAmount
                            ? `${tBilling('package_details.service_amount', { defaultValue: 'Service Amount' })} (${servPercent}%)`
                            : `${tBilling('package_details.service_percentage', { defaultValue: 'Service Percentage' })} (${servAmt})`
                        }
                        name={isAmount ? 'package_definition.service_amount' : 'package_definition.service_percent'}
                        value={serviceDisplay}
                        inputProps={{
                          min: 0,
                          max: isAmount ? pkgAmt : 100.00,
                          step: 'any',
                        }}
                        onChange={e => {
                          if (isAmount) {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const newService = Math.max(0, Math.min(rounded, pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newService).toFixed(2))) : 0;
                            setFieldValue('package_definition.service_amount', newService);
                            setFieldValue('package_definition.pharmacy_amount', complement);
                            setFieldValue('package_definition.service_percent', pkgAmt ? Number(((newService / pkgAmt) * 100).toFixed(2)) : 0);
                            setFieldValue('package_definition.pharmacy_percent', pkgAmt ? Number(((complement / pkgAmt) * 100).toFixed(2)) : 0);
                          } else {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const percent = Math.max(0, Math.min(rounded, 100));
                            const complementPercent = Math.max(0, Number((100 - percent).toFixed(2)));
                            const rawAmt = pkgAmt ? (pkgAmt * percent) / 100 : 0;
                            const newService = Math.max(0, Math.min(Number(rawAmt.toFixed(2)), pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newService).toFixed(2))) : 0;
                            setFieldValue('package_definition.service_percent', percent);
                            setFieldValue('package_definition.pharmacy_percent', complementPercent);
                            setFieldValue('package_definition.service_amount', newService);
                            setFieldValue('package_definition.pharmacy_amount', complement);
                          }
                        }}
                        onBlur={handleBlur}
                        InputProps={isAmount ? undefined : { endAdornment: <span>%</span> }}
                        error={(touched.package_definition?.service_amount && Boolean(errors.package_definition?.service_amount)) || (touched.package_definition?.service_amount && typeof errors.package_definition === 'string')}
                  
                      />
                    </Grid2>

                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        data-testid="pkgdef-pharmacy-distribution-input"
                        fullWidth

                        disabled={ !values.package_definition.distribution_type || values.package_definition.is_finalized}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={values.package_definition.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        label={
                          isAmount
                            ? `${tBilling('package_details.pharmacy_amount', { defaultValue: 'Pharmacy Amount' })} (${pharmPercent}%)`
                            : `${tBilling('package_details.pharmacy_percentage', { defaultValue: 'Pharmacy Percentage' })} (${pharmAmt})`
                        }
                        name={isAmount ? 'package_definition.pharmacy_amount' : 'package_definition.pharmacy_percent'}
                        color="error"
                        size="small"
                        value={pharmacyDisplay}
                        inputProps={{
                          min: 0,
                          max: isAmount ? pkgAmt : 100.00,
                          step: 'any',
                        }}
                        onChange={e => {
                          if (isAmount) {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const newPharmacy = Math.max(0, Math.min(rounded, pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newPharmacy).toFixed(2))) : 0;
                            setFieldValue('package_definition.pharmacy_amount', newPharmacy);
                            setFieldValue('package_definition.service_amount', complement);
                            setFieldValue('package_definition.pharmacy_percent', pkgAmt ? Number(((newPharmacy / pkgAmt) * 100).toFixed(2)) : 0);
                            setFieldValue('package_definition.service_percent', pkgAmt ? Number(((complement / pkgAmt) * 100).toFixed(2)) : 0);
                          } else {
                            const rounded = Number((Number(e.target.value) || 0).toFixed(2));
                            const percent = Math.max(0, Math.min(rounded, 100));
                            const complementPercent = Math.max(0, Number((100 - percent).toFixed(2)));
                            const rawAmt = pkgAmt ? (pkgAmt * percent) / 100 : 0;
                            const newPharmacy = Math.max(0, Math.min(Number(rawAmt.toFixed(2)), pkgAmt || 0));
                            const complement = pkgAmt ? Math.max(0, Number((pkgAmt - newPharmacy).toFixed(2))) : 0;
                            setFieldValue('package_definition.pharmacy_percent', percent);
                            setFieldValue('package_definition.service_percent', complementPercent);
                            setFieldValue('package_definition.pharmacy_amount', newPharmacy);
                            setFieldValue('package_definition.service_amount', complement);
                          }
                        }}
                        onBlur={handleBlur}
                        error={(touched.package_definition?.pharmacy_amount && Boolean(errors.package_definition?.pharmacy_amount)) || (touched.package_definition?.pharmacy_amount && typeof errors.package_definition === 'string')}
                        InputProps={isAmount ? undefined : { endAdornment: <span>%</span> }}
                  
                      />
                    </Grid2>
                  </Grid2>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, gap: 2 }}>
                  <Button
                    data-testid="pkgdef-services-tab-button"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: 16,
                      color: activeTab === 'services' ? '#e57353' : '#bdbdbd',
                      borderColor: activeTab === 'services' ? '#e57353' : '#bdbdbd',
                      backgroundColor: '#fff',
                    }}
                    onClick={() => setActiveTab('services')}
                  >
                    {tBilling('package_config.services', { defaultValue: 'Services' })}
                  </Button>
                  <Button
                    data-testid="pkgdef-pharmacy-tab-button"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      fontSize: 16,
                      color: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
                      borderColor: activeTab === 'pharmacy' ? '#e57353' : '#bdbdbd',
                      backgroundColor: '#fff',
                    }}
                    onClick={() => setActiveTab('pharmacy')}
                  >
                    {tBilling('package_config.pharmacy_items', { defaultValue: 'Pharmacy Items' })}
                  </Button>
                </Box>


                {/* Services Section */}

                {activeTab === 'services' && (
                  <Box sx={{p: 2}}>
                    <FieldArray name="service_items">

                      {({ push, remove }: { push: (v: ServiceItem) => void; remove: (idx: number) => void }) => 
                      {
                        const PackageServicesList = Array.isArray(servicesList) ? servicesList: [];

                        return(
                          <>
                            <Grid2 container spacing={2} alignItems="center" justifyContent="space-between">

                              <Grid2 size={{xl:4, lg:4, md:6, xs:12}}>
                                <Autocomplete
                                  data-testid="pkgdef-procedure-select"
                                  multiple
                                  fullWidth
                                  size="small"
                                  disabled={values.package_definition.is_finalized}
                                  sx={values.package_definition.is_finalized ? {
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  } : undefined}
                                  options={ProcedureLists?.items || []}
                                  getOptionLabel={option => option.name_label || option.name}
                                  filterOptions={(x) => x}
                                  onInputChange={(_, newInputValue, reason) => {
                                    if (reason === 'input') {
                                      setProcedureSearch(newInputValue);
                                    }
                                  }}
                                  value={selectedProcedures}
                                  onChange={(_, newValue, reason, details) => {
                                    const newProcedures = newValue as ProcedureItem[];
                                     
                                    setSelectedProcedures(newProcedures);
                                    // Sync procedure_details to Formik for validation
                                    setFieldValue('package_definition.procedure_details', newProcedures.map(p => ({ procedure_id: p.id })));
                                    
                                    if (reason === 'removeOption' && details?.option) {
                                      // A procedure was removed - remove its services or decrease quantity if shared
                                      const removedProcedure = details.option as ProcedureItem;
                                      const currentServices = values.service_items || [];
                                      const updatedServices = removeServicesForProcedure(currentServices, removedProcedure.id);
                                      setFieldValue('service_items', updatedServices.length > 0 ? updatedServices : []);
                                      
                                      // Recalculate total with updated services
                                      const totalAmount = updatedServices.reduce((sum, s) => sum + Number(s.total_amount || 0), 0);
                                      setFieldValue('package_definition.total_service_amount', totalAmount);
                                      
                                      // Remove from loaded IDs so it can be re-added
                                      setLoadedProcedureIds(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(removedProcedure.id);
                                        return newSet;
                                      });
                                      
                                      // Also update existingProcedureDetails to remove the deleted procedure
                                      setExistingProcedureDetails(prev => prev.filter(pd => pd.procedure_id !== removedProcedure.id));
                                    } else if (reason === 'clear') {
                                      // All procedures cleared
                                      setFieldValue('service_items', []);
                                      setFieldValue('package_definition.total_service_amount', 0);
                                      setFieldValue('package_definition.procedure_details', []);
                                      setLoadedProcedureIds(new Set());
                                      setExistingProcedureDetails([]);
                                    }
                                    // For 'selectOption' - default services will be loaded via the useAppQuery
                                  }}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      label={RequiredLabel(tBilling('package_config.cycle', { defaultValue: 'Cycle' }), false)}
                                      placeholder={selectedProcedures.length === 0 ? tBilling('package_config.select_cycle', { defaultValue: 'Select Cycle' }) : ''}
                                      size="small"
                                      onBlur={() => setTouched({ ...touched, package_definition: { ...touched.package_definition, procedure_details: true } })}
                                      error={touched.package_definition?.procedure_details && Boolean(errors.package_definition?.procedure_details)}
                                      helperText={touched.package_definition?.procedure_details && errors.package_definition?.procedure_details ? String(errors.package_definition.procedure_details) : undefined}
                                    />
                                  )}
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                              </Grid2>

                              <Grid2 size={{xl:4, lg:6, md:4, xs:12}}>
                                <Autocomplete
                                  disablePortal
                                  fullWidth
                                  size="small"
                                  disabled={values.package_definition.is_finalized}
                                  sx={values.package_definition.is_finalized ? {
                                    backgroundColor: '#fafafa',
                                    opacity: 0.9,
                                    transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                  } : undefined}
                                  options={inputValueForService.trim() ? PackageServicesList:[]}
                                  open={PackageServicesList.length > 0 && inputValueForService.trim().length > 0}
                                  value={selectedService}
                                  inputValue={inputValueForService}
                                  onChange={(e, value) => {
                                    setSelectedService(value);
                                    if (value) {
                                      const v = value as ServiceMasterItem;
                                      
                                      // Check if service already exists (prevent duplicates)
                                      const currentServices = values.service_items || [];
                                      const existingServiceIndex = currentServices.findIndex(
                                        s => s.service === v.id && s.service_name
                                      );
                                      
                                      if (existingServiceIndex >= 0) {
                                        // Service already exists - increase quantity instead of adding duplicate
                                        const existingService = currentServices[existingServiceIndex];
                                        const newQuantity = (Number(existingService.quantity) || 1) + 1;
                                        setFieldValue(`service_items[${existingServiceIndex}].quantity`, newQuantity);
                                        // Recalculate total_amount
                                        const newTotal = Number(existingService.unit_rate) * newQuantity * (1 - Number(existingService.discount_percent) / 100);
                                        setFieldValue(`service_items[${existingServiceIndex}].total_amount`, Number(newTotal.toFixed(2)));
                                      } else if (
                                        values.service_items &&
                                        values.service_items.length > 0 &&
                                        !values.service_items[0].service_name
                                      ) {
                                        // First row is empty placeholder - fill it
                                        setFieldValue('service_items[0].service', v.id);
                                        setFieldValue('service_items[0].service_name', v.name);
                                        setFieldValue('service_items[0].adjustable_head', false);
                                        setFieldValue('service_items[0].is_consumables', false);
                                        setFieldValue('service_items[0].procedure', '');
                                        setFieldValue('service_items[0].gender', 'both');
                                        setFieldValue('service_items[0].discount_percent', 0);
                                        setFieldValue('service_items[0].quantity', 1);
                                        setFieldValue('service_items[0].unit_rate', Number(v.charges || 0));
                                        setFieldValue('service_items[0].original_unit_rate', Number(v.charges || 0));
                                        setFieldValue('service_items[0].original_quantity', 1);
                                        setFieldValue('service_items[0].unit_percentage', 0);
                                        setFieldValue('service_items[0].discount_amount', 0);
                                        setFieldValue('service_items[0].discount_on_quantity', false);
                                        setFieldValue('service_items[0].unlimited_qty', false);
                                        setFieldValue('service_items[0].service_source_type', v.service_type);
                                        setFieldValue('service_items[0].category', '');
                                        setFieldValue('service_items[0].adjustable_head_type', '');
                                        setFieldValue('service_items[0].total_amount', 0);
                                      } else {
                                        // Add new service row
                                        push({
                                          service: v.id,
                                          service_name: v.name,
                                          adjustable_head: false,
                                          is_consumables: false,
                                          procedure: '',
                                          gender: 'both',
                                          discount_percent: 0,
                                          quantity: 1,
                                          unit_rate: v.charges || 0,
                                          original_unit_rate: v.charges || 0,
                                          original_quantity: 1,
                                          unit_percentage: 0,
                                          discount_amount: 0,
                                          discount_on_quantity: false,
                                          unlimited_qty: false,
                                          service_source_type: v.service_type,
                                          category: '',
                                          adjustable_head_type: '',
                                          total_amount: 0
                                        });
                                      }
                                      setTimeout(() => {
                                        setInputValueForService('');
                                        setSelectedService(null);
                                      }, 0);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    setInputValueForService(value);
                                    setServiceSearch(value);
                                  }}
                                  getOptionLabel={option => option.name}

                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      label={tBilling('package_details.search_service', { defaultValue: 'Search Service' })}
                                      placeholder={tBilling('package_details.type_to_search', { defaultValue: 'Type to search...' })}
                                      size="small"
                                    />
                                  )}
                                  noOptionsText={inputValueForService.trim() && PackageServicesList.length === 0 ? tBilling('package_config.no_related_option_for_search', { defaultValue: 'No related option for search' }) : ''}
                                />
                              </Grid2>
                            </Grid2>

                            {/* Service Name Header with Adjustable Head, Is Consumables, and Amount */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',backgroundColor: '#f5f5f5', borderRadius: 1, px: 2, py: 1, mt: 3, mb: 1 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>{tBilling('package_details.service_name', { defaultValue: 'Service Name' })}</Typography>
                              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>
                                {tBilling('package_details.total_services_amount', { defaultValue: 'Total Services Amount' })} : {currencySymbol}{Number(values.package_definition.total_service_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            </Box>

                            {/* Adjustable Head, Is Consumables, Enter Amount Row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="package_definition.adjustable_head"
                                    color="success"
                                    icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                    checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                    disabled={values.package_definition.is_finalized}
                                    checked={values.package_definition.adjustable_head || false}
                                    onChange={(e) => {
                                      setFieldValue('package_definition.adjustable_head', e.target.checked);
                                    }}
                                  />
                                }
                                label={tBilling('package_details.adjustable_head', { defaultValue: 'Adjustable Head' })}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="package_definition.is_consumables"
                                    color="success"
                                    icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                    checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                    disabled={values.package_definition.is_finalized}
                                    checked={values.package_definition.is_consumables || false}
                                    onChange={(e) => {
                                      setFieldValue('package_definition.is_consumables', e.target.checked);
                                      if (!e.target.checked) {
                                        setFieldValue('package_definition.consumable_amount', 0);
                                      }
                                    }}
                                  />
                                }
                                label={tBilling('package_details.is_consumables', { defaultValue: 'Is Consumables' })}
                              />
                              <TextField
                                name="package_definition.consumable_amount"
                                label={tBilling('package_details.enter_amount', { defaultValue: 'Enter Amount' })}
                                placeholder={tBilling('package_details.enter_amount', { defaultValue: 'Enter Amount' })}
                                size="small"
                                type="number"
                                disabled={values.package_definition.is_finalized ||  !values.package_definition.is_consumables }
                                value={values.package_definition.consumable_amount || ''}
                                style={{cursor:!values.package_definition.is_consumables? 'no-drop':'auto'}}
                                onChange={(e) => setFieldValue('package_definition.consumable_amount', Number(e.target.value) || 0)}
                                sx={{ width: 200 }}
                              />
                            </Box>

                            {values.service_items && values.service_items.length > 0 ? (
                              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2,backgroundColor: '#f6f5f540', p: 2 }}>
                                {values.service_items.map((item: ServiceItem, idx: number) => 
                                {
                                  const rawTotalAmount =
                                  (((Number(item.unit_rate) || 0) -
                                    (Number(item.discount_amount) || 0)) *
                                  (Number(item.quantity)  || 0));

                                  // 2) Store numeric once, only if changed
                                  const fixedTotal = Number(rawTotalAmount.toFixed(2));
                                  if (item.total_amount !== fixedTotal) {
                                    setFieldValue(`service_items[${idx}].total_amount`, fixedTotal);
                                  }
                                  
                                  // Auto-calculate unit_percentage if not already set or when unit_rate changes
                                  const unitRate = Number(item.unit_rate) || 0;
                                  const qty = Number(item.quantity) || 1;
                                  if (servAmt > 0 && qty > 0 && unitRate > 0) {
                                    const calculatedPercent = Number(((unitRate * qty * 100) / servAmt).toFixed(2));
                                    if (item.unit_percentage !== calculatedPercent) {
                                      setFieldValue(`service_items[${idx}].unit_percentage`, calculatedPercent);
                                    }
                                  }
                                  
                                  // After ensuring each item's total_amount is up to date:
                                  const totalServicesAmount = (values.service_items || []).reduce((sum, si) => {
                                    const rowTotal = Number(si.total_amount) || 0;
                                    return sum + rowTotal;
                                  }, 0);

                                  // Persist to Formik: package_definition.total_service_amount
                                  const totalServicesFixed = Number(totalServicesAmount.toFixed(2));
                                  if (values.package_definition.total_service_amount !== totalServicesFixed) {
                                    setFieldValue('package_definition.total_service_amount', totalServicesFixed);
                                  }
                                  return (
                                    <React.Fragment key={idx}>
                                      <Box sx={{ py: 2 }}>
                                        {/* Service Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, px: 2, py: 1, mb: 2 }}>
                                          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>
                                            {item.service_name || `${tBilling('package_details.service', { defaultValue: 'Service' })} ${idx + 1}`}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            {/* Service From No. of Cycles badge */}
                                            {item.procedure_source_ids && item.procedure_source_ids.length > 0 && (
                                              <Box
                                                role="button"
                                                tabIndex={0}
                                                aria-label={tBilling('package_details.click_to_see_cycle_details', { defaultValue: 'Click to see cycle details' })}
                                                onClick={(e) => handleCyclePopoverOpen(e, idx)}
                                                onKeyDown={(e) => handleCyclePopoverKeyDown(e, idx)}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                  px: 1.5,
                                                  py: 0.5,
                                                  borderRadius: 2,
                                                  bgcolor: '#e3f2fd',
                                                  cursor: 'pointer',
                                                  '&:hover': { bgcolor: '#bbdefb' },
                                                  '&:focus': { outline: '2px solid #1976d2', outlineOffset: 2 },
                                                }}
                                              >
                                                <Typography sx={{ fontSize: 12, color: '#1976d2', fontWeight: 500 }}>
                                                  {tBilling('package_details.service_from_no_cycle', { defaultValue: 'Service From No. Cycle' })}:
                                                </Typography>
                                                <Box
                                                  sx={{
                                                    backgroundColor: '#1976d2',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    width: 22,
                                                    height: 22,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {item.procedure_source_ids.length}
                                                </Box>
                                              </Box>
                                            )}
                                            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>
                                              {tBilling('package_details.service_amount', { defaultValue: 'Service Amount' })}: {currencySymbol}{(((Number(item.unit_rate) || 0) - (Number(item.discount_amount) || 0)) * (Number(item.quantity) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        {/* Popover for cycle details */}
                                        <Popover
                                          open={cyclePopoverAnchor.element !== null && cyclePopoverAnchor.serviceIdx === idx}
                                          anchorEl={cyclePopoverAnchor.element}
                                          onClose={handleCyclePopoverClose}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                          }}
                                          PaperProps={{
                                            sx: {
                                              backgroundColor: '#fff',
                                              color: '#222',
                                              borderRadius: 2,
                                              minWidth: 200,
                                              maxWidth: 320,
                                              p: 1,
                                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                            },
                                          }}
                                        >
                                          <Typography sx={{ fontSize: 12, color: '#1976d2', px: 1, pt: 0.5 }}>
                                            {tBilling('package_details.cycle_details', { defaultValue: 'CYCLE DETAILS' })} ({item.procedure_source_ids?.length || 0})
                                          </Typography>
                                          <List dense sx={{ py: 0 }}>
                                            {getProcedureNamesFromIds(item.procedure_source_ids || []).map((name, i) => (
                                              <ListItem key={i} sx={{ py: 0.5 }}>
                                                <ListItemText
                                                  primary={name}
                                                  primaryTypographyProps={{ sx: { fontSize: 14, color: '#222' } }}
                                                />
                                              </ListItem>
                                            ))}
                                          </List>
                                        </Popover>

                                        {/* Unlimited Quantity Checkbox */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                                checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                                disabled={values.package_definition.is_finalized}
                                                name={`service_items[${idx}].unlimited_qty`}
                                                color="success"
                                                checked={Boolean(item.unlimited_qty)}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                  setFieldValue(`service_items[${idx}].unlimited_qty`, e.target.checked);
                                                  if (e.target.checked) {
                                                    // Store the original values before setting to 0
                                                    if (!item.original_unit_rate) {
                                                      setFieldValue(`service_items[${idx}].original_unit_rate`, item.unit_rate);
                                                    }
                                                    if (!item.original_quantity) {
                                                      setFieldValue(`service_items[${idx}].original_quantity`, item.quantity);
                                                    }
                                                    setFieldValue(`service_items[${idx}].quantity`, 0);
                                                    setFieldValue(`service_items[${idx}].discount_amount`, 0);
                                                    setFieldValue(`service_items[${idx}].unit_rate`, 0);
                                                    setFieldValue(`service_items[${idx}].unit_percentage`, 0);
                                                  } else {
                                                    // Restore quantity and unit_rate to original values
                                                    const originalQty = item.original_quantity ?? 1;
                                                    const originalRate = item.original_unit_rate ?? 0;
                                                    setFieldValue(`service_items[${idx}].quantity`, originalQty);
                                                    setFieldValue(`service_items[${idx}].unit_rate`, originalRate);
                                                  }
                                                }}
                                              />
                                            }
                                            label={tBilling('package_details.unlimited_quantity', { defaultValue: 'Unlimited Quantity' })}
                                          />
                                        </Box>

                                        {/* Service Fields Row */}  
                                        <Grid2 container spacing={2.5} alignItems="center">
                                          <Grid2 size={2.5}>
                                            <Autocomplete
                                              options={billingConfig?.PACKAGE_PROCEDURE_MASTER || []}
                                              getOptionLabel={option => String(option.name)}
                                              disabled={values.package_definition.is_finalized}
                                              sx={values.package_definition.is_finalized ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 0.9,
                                              } : undefined}
                                              value={billingConfig?.PACKAGE_PROCEDURE_MASTER?.find(opt => opt.code === item.procedure) || null}
                                              onChange={(_, newValue) => setFieldValue(`service_items[${idx}].procedure`, newValue ? newValue.code : '')}
                                              renderInput={params => (
                                                <TextField
                                                  {...params}
                                                  label={RequiredLabel(tBilling('package_details.procedure', { defaultValue: 'Procedure' }), false)}
                                                  size="small"
                                                  error={Boolean(Array.isArray(touched.service_items) && touched.service_items[idx]?.procedure && Array.isArray(errors.service_items) && typeof errors.service_items[idx] === 'object' && errors.service_items[idx] !== null && 'procedure' in errors.service_items[idx])}
                                                />
                                              )}
                                              isOptionEqualToValue={(option, value) => option.code === value.code}
                                            />
                                          </Grid2>
                                          <Grid2 size={2.5}>
                                            <Autocomplete
                                              options={[
                                                { value: 'both', label: 'Both (M&F)' },
                                                { value: 'male', label: 'Male' },
                                                { value: 'female', label: 'Female' },
                                              ]}
                                              getOptionLabel={option => option.label}
                                              value={(() => {
                                                const opts = [
                                                  { value: 'both', label: 'Both (M&F)' },
                                                  { value: 'male', label: 'Male' },
                                                  { value: 'female', label: 'Female' },
                                                ];
                                                return opts.find(opt => opt.value === item.gender) || null;
                                              })()}
                                              onChange={(_, newValue) => setFieldValue(`service_items[${idx}].gender`, newValue ? newValue.value : '')}
                                              disabled={values.package_definition.is_finalized}
                                              sx={values.package_definition.is_finalized ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 0.9,
                                              } : undefined}
                                              renderInput={params => (
                                                <TextField
                                                  {...params}
                                                  label={tBilling('package_details.gender', { defaultValue: 'Gender' })}
                                                  size="small"
                                                />
                                              )}
                                              isOptionEqualToValue={(option, value) => option.value === value.value}
                                            />
                                          </Grid2>
                                          <Grid2 size={2}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label={tBilling('package_details.quantity', { defaultValue: 'Quantity' })}
                                              type="number"
                                              value={item.quantity}
                                              name={`service_items[${idx}].quantity`}
                                              onChange={handleChange}
                                              inputProps={{ min: 1 }}
                                              disabled={values.package_definition.is_finalized || item.unlimited_qty}
                                              sx={values.package_definition.is_finalized ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 0.9,
                                              } : undefined}
                                            />
                                          </Grid2>
                                          <Grid2 size={2}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label={`${tBilling('package_details.unit_rate', { defaultValue: 'Service Rate' })}(${currencySymbol})`}
                                              type="number"
                                              value={item.unit_rate}
                                              name={`service_items[${idx}].unit_rate`}
                                              inputProps={{ min: 0 }}
                                              disabled={values.package_definition.is_finalized || item.unlimited_qty}
                                              sx={values.package_definition.is_finalized ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 0.9,
                                              } : undefined}
                                              onChange={e => {
                                                if (values.package_definition.is_finalized || item.unlimited_qty) return;
                                                handleChange(e);
                                                const newRate = Number(e.target.value) || 0;
                                                const qty = Number(item.quantity) || 1;
                                                if (servAmt > 0 && qty > 0) {
                                                  const percent = (newRate * qty * 100) / servAmt;
                                                  setFieldValue(
                                                    `service_items[${idx}].unit_percentage`,
                                                    Number(percent.toFixed(2))
                                                  );
                                                }
                                              }}
                                            />
                                          </Grid2>
                                          <Grid2 size={2}>
                                            <TextField
                                              fullWidth
                                              size="small"
                                              label={tBilling('package_details.unit_percentage', { defaultValue: 'Unit Percentage' })}
                                              type="number"
                                              placeholder="00"
                                              value={item.unit_percentage || ''}
                                              name={`service_items[${idx}].unit_percentage`}
                                              inputProps={{ min: 0, max: 100 }}
                                              disabled={values.package_definition.is_finalized || item.unlimited_qty || isAmount}
                                              sx={(values.package_definition.is_finalized || isAmount) ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 1,
                                              } : undefined}
                                              onChange={e => {
                                                if (values.package_definition.is_finalized || item.unlimited_qty || isAmount) return;
                                                handleChange(e);
                                                const percent = Number(e.target.value) || 0;
                                                const qty = Number(item.quantity) || 1;
                                                if (servAmt > 0 && qty > 0) {
                                                  const newRate = (percent * servAmt) / (qty * 100);
                                                  setFieldValue(
                                                    `service_items[${idx}].unit_rate`,
                                                    Number(newRate.toFixed(2))
                                                  );
                                                }
                                              }}
                                            />
                                          </Grid2>
                                          <Grid2 size={1} display="flex" justifyContent="end">
                                            <IconButton 
                                              onClick={() => {
                                                handleDeleteClick(() => {
                                                  if (idx === 0 && values.service_items.length === 1) {
                                                    setFieldValue('service_items[0].service', '');
                                                    setFieldValue('service_items[0].service_name', '');
                                                    setFieldValue('service_items[0].adjustable_head', false);
                                                    setFieldValue('service_items[0].is_consumables', false);
                                                    setFieldValue('service_items[0].procedure', '');
                                                    setFieldValue('service_items[0].gender', 'both');
                                                    setFieldValue('service_items[0].discount_percent', 0);
                                                    setFieldValue('service_items[0].quantity', 1);
                                                    setFieldValue('service_items[0].unit_rate', 0);
                                                    setFieldValue('service_items[0].unit_percentage', 0);
                                                    setFieldValue('service_items[0].discount_amount', 0);
                                                    setFieldValue('service_items[0].discount_on_quantity', false);
                                                    setFieldValue('service_items[0].unlimited_qty', false);
                                                    setFieldValue('service_items[0].service_source_type', '');
                                                    setFieldValue('service_items[0].category', '');
                                                    setFieldValue('service_items[0].adjustable_head_type', '');
                                                    setFieldValue('service_items[0].total_amount', 0);
                                                  } else {
                                                    remove(idx);
                                                  }
                                                });
                                              }}
                                              sx={{ color: '#e57373' }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Grid2>
                                        </Grid2>
                                      </Box>
                                      {idx < values.service_items.length - 1 && (
                                        <Divider sx={{ borderColor: '#6a6868ff', borderBottomWidth: 1.5 }} />
                                      )}
                                    </React.Fragment>
                                  );
                                
                                }
                          
                                )
                                }
                              </Box>


                            ) : (
                              <Typography color="text.secondary" sx={{ ml: 1 }}>{tBilling('package_details.no_services_added', { defaultValue: 'No services added' })}.</Typography>
                            )}
                          </>
                        );
                      }
                      }
                    </FieldArray>
                  </Box>
                )}

                {/* Pharmacy Items Section */}

                {activeTab === 'pharmacy' && (
                  <Box sx={{p: 2 }}>
                    <FieldArray name="pharmacy_items">
                      {({ push, remove }) => {
                      // Autocomplete state for adding new item

                        // Pharmacy list from API
                        const pharmacyItems = Array.isArray(pharmacyList?.objects) ? pharmacyList.objects : [];
                        const isSetAllItems = values.package_definition?.pharmacy_set_all_items || false;
                        const isPharmacyDisabled = values.package_definition.is_finalized || isSetAllItems;
                        return (
                          <>
                            <Box sx={{my: 3}}>

                            
                              <Grid2 container spacing={2} alignItems="center" justifyContent="space-between">
                                {/* Set All Items Checkbox - Left Side */}
                                <Grid2>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                      checked={isSetAllItems}
                                      disabled={values.package_definition.is_finalized}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setFieldValue('package_definition.pharmacy_set_all_items', checked);
                                        if (checked) {
                                          const currentItems = values.pharmacy_items ?? [];
                                          const hasValidItems = currentItems.some(item => item.item && item.item !== 0);
                                          if (hasValidItems) {
                                            setSavedPharmacyItems(currentItems);
                                          }
                                          setFieldValue('pharmacy_items', [{
                                            item: 0,
                                            item_name: '',
                                            quantity: 0,
                                            uom: null,
                                            discount_percent: 0,
                                            uom_dropdown: null,
                                          }]);
                                          setSelectedDrug(null);
                                          setInputValue('');
                                          setSelectedItemGroupCode('');
                                          setSelectedItemCategoryCode('');
                                        } else {
                                          if (savedPharmacyItems && savedPharmacyItems.length > 0) {
                                            setFieldValue('pharmacy_items', savedPharmacyItems);
                                            setSavedPharmacyItems(null);
                                          }
                                        }
                                      }}
                                      icon={<span className={styles.AutoCompleteDropDownIcon} />}
                                      checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                                    />
                                    <Typography>{tBilling('package_details.set_all_items', { defaultValue: 'Set All Items' })}</Typography>
                                  </Box>
                                </Grid2>
                                {/* Dropdowns - Right Side */}
                                <Grid2>
                                  <Grid2 container spacing={2} alignItems="center">
                                    <Grid2 size={{xl:'auto', lg:'auto', md:'auto', xs:12}}>
                                      <Autocomplete
                                        disabled={isPharmacyDisabled}
                                        sx={isPharmacyDisabled ? {
                                          backgroundColor: '#fafafa',
                                          opacity: 0.9,
                                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                          minWidth: 180,
                                        } : { minWidth: 180 }}
                                        fullWidth
                                        size="small"
                                        options={billingConfig?.item_group || []}
                                        getOptionLabel={option => option.name}
                                        value={billingConfig?.item_group?.find(opt => opt.code === selectedItemGroupCode) || null}
                                        onChange={(_e, newValue) => setSelectedItemGroupCode(newValue ? newValue.code : '')}
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            label={tBilling('package_details.item_group', { defaultValue: 'Item Group' })}
                                            placeholder={tBilling('package_details.select_item_group', { defaultValue: 'Select Item Group' })}
                                            size="small"
                                          />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.code === value.code}
                                      />
                                    </Grid2>
                                    <Grid2 size={{xl:'auto', lg:'auto', md:'auto', xs:12}}>
                                      <Autocomplete
                                        fullWidth
                                        size="small"
                                        disabled={isPharmacyDisabled}
                                        sx={isPharmacyDisabled ? {
                                          backgroundColor: '#fafafa',
                                          opacity: 0.9,
                                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                          minWidth: 180,
                                        } : { minWidth: 180 }}
                                        options={billingConfig?.item_category || []}
                                        getOptionLabel={option => option.name}
                                        value={billingConfig?.item_category?.find(opt => opt.code === selectedItemCategoryCode) || null}
                                        onChange={(_e, newValue) => setSelectedItemCategoryCode(newValue ? newValue.code : '')}
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            label={tBilling('package_details.item_category', { defaultValue: 'Item Category' })}
                                            placeholder={tBilling('package_details.select_item_category', { defaultValue: 'Select Item Category' })}
                                            size="small"
                                          />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.code === value.code}
                                      />
                                    </Grid2>
                                    <Grid2 size={{xl:'auto', lg:'auto', md:'auto', xs:12}}>
                                      <Autocomplete
                                        fullWidth
                                        size="small"
                                        disabled={isPharmacyDisabled}
                                        sx={isPharmacyDisabled ? {
                                          backgroundColor: '#fafafa',
                                          opacity: 0.9,
                                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                          minWidth: 250,
                                        } : { minWidth: 250 }}
                                        options={pharmacyItems}
                                        openOnFocus
                                        filterOptions={(x) => x}
                                        value={selectedDrug}
                                        inputValue={inputValue}
                                        onChange={(e, value) => {
                                          setSelectedDrug(value);
                                          if (value) {
                                          // If first row is blank, update it
                                            if (
                                              values.pharmacy_items &&
                                            values.pharmacy_items.length > 0 &&
                                            !values.pharmacy_items[0].item_name
                                            ) {
                                              const v = value as PharmacyItemType;
                                              setFieldValue('pharmacy_items[0].item_name', v.name_label || v.name || '');
                                              setFieldValue('pharmacy_items[0].item', v.id );
                                              setFieldValue('pharmacy_items[0].quantity', 1);
                                              setFieldValue('pharmacy_items[0].uom', 'uom' in v && typeof v.uom === 'string' ? v.uom : '');
                                              setFieldValue('pharmacy_items[0].discount_percent', 0);
                                              setFieldValue('pharmacy_items[0].uom_dropdown', v.uom_conversion || null);
                                            } else {
                                              const v = value as PharmacyItemType;
                                              push({
                                                item: v.id,
                                                item_name: v.name_label || v.name || '',
                                                quantity: 1,
                                                uom: 'uom' in v && typeof v.uom === 'string' ? v.uom : '',
                                                discount_percent: 0,
                                                uom_dropdown: v.uom_conversion || null,
                                              });
                                            }
                                            setTimeout(() => {
                                              setInputValue('');
                                              setSelectedDrug(null);
                                            }, 0);
                                          }
                                        }}
                                        onInputChange={(e, value) => {
                                          setInputValue(value);
                                          setPharmacySearch(value);
                                          refetchPharmacyList();
                                        }}
                                        getOptionLabel={option => typeof option === 'string' ? option : option.name_label || option.name || ''}
                                        noOptionsText={inputValue.trim() ? tBilling('package_config.no_matching_items', { defaultValue: 'No matching items' }) : tBilling('package_details.type_to_search', { defaultValue: 'Type to search...' })}
                                        renderOption={(props, option) => (
                                          <li {...props}>
                                            {option.name_label} {option.molecule && `(${option.molecule})`}
                                          </li>
                                        )}
                                        renderInput={params => (
                                          <TextField
                                            {...params}
                                            label={tBilling('package_details.search_pharmacy_item', { defaultValue: 'Search Pharmacy Item' })}
                                            placeholder={tBilling('package_details.type_to_search', { defaultValue: 'Type to search...' })}
                                            size="small"
                                          />
                                        )}
                                      />
                                    </Grid2>
                                  </Grid2>
                                </Grid2>
                              </Grid2>
                            </Box>
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.item_name', { defaultValue: 'Item Name' })}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.quantity', { defaultValue: 'Quantity' })}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.uom', { defaultValue: 'UOM' })}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{tBilling('package_details.discount', { defaultValue: 'Discount' })} (%)</TableCell>
                                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>{tBilling('package_details.action', { defaultValue: 'Action' })}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {values.pharmacy_items && values.pharmacy_items.length > 0 && values.pharmacy_items.map((item, idx) => {
                                    // Pharmacy item discount percent validation based on CONCESSION rule
                                    const itemDiscountPercent = Number(item.discount_percent) || 0;
                                    const isPharmacyDiscountBelowMin = concessionRule && itemDiscountPercent > 0 && itemDiscountPercent < concessionMinPercent;
                                    const isPharmacyDiscountAboveMax = concessionRule && itemDiscountPercent > concessionMaxPercent;
                                    const isPharmacyDiscountOutOfRange = isPharmacyDiscountBelowMin || isPharmacyDiscountAboveMax;
                                    const pharmacyDiscountValidationMessage = isPharmacyDiscountOutOfRange
                                      ? tBilling('package_details.concession_validation_error', {
                                        min: concessionMinPercent.toFixed(2),
                                        max: concessionMaxPercent.toFixed(2),
                                        defaultValue: `Discount must be between ${concessionMinPercent.toFixed(2)}% and ${concessionMaxPercent.toFixed(2)}%`
                                      })
                                      : '';
                                    
                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          <TextField
                                            disabled={isPharmacyDisabled}
                                            sx={isPharmacyDisabled ? {
                                              backgroundColor: '#fafafa',
                                              opacity: 0.9,
                                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                            } : undefined}
                                            fullWidth
                                            size="small"
                                            placeholder={`${tBilling('package_config.item', { defaultValue: 'Item' })} ${idx + 1}`}
                                            name={`pharmacy_items[${idx}].item_name`}
                                            value={item.item_name}
                                            onChange={handleChange}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            disabled={isPharmacyDisabled}
                                            sx={isPharmacyDisabled ? {
                                              backgroundColor: '#fafafa',
                                              opacity: 0.9,
                                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                            } : undefined}
                                            placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                                            type="number"
                                            name={`pharmacy_items[${idx}].quantity`}
                                            value={item.quantity}
                                            onChange={handleChange}
                                            inputProps={{ min: 1 }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Autocomplete
                                            disabled={isPharmacyDisabled}
                                            sx={isPharmacyDisabled ? {
                                              backgroundColor: '#fafafa',
                                              opacity: 0.9,
                                              transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                            } : undefined}
                                            options={values.pharmacy_items?.[idx]?.uom_dropdown || []}
                                            getOptionLabel={option => option.from_uom_label}
                                            value={values.pharmacy_items?.[idx]?.uom_dropdown?.find(opt => opt.from_uom === item.uom) || null}
                                            onChange={(_, newValue) => setFieldValue(`pharmacy_items[${idx}].uom`, newValue ? newValue.from_uom  : '')}
                                            renderInput={params => (
                                              <TextField
                                                {...params}
                                                placeholder={tBilling('package_details.select_uom', { defaultValue: 'Select UOM' })}
                                                size="small"
                                                error={Boolean(Array.isArray(touched.pharmacy_items) && touched.pharmacy_items[idx]?.uom && Array.isArray(errors.pharmacy_items) && typeof errors.pharmacy_items[idx] === 'object' && errors.pharmacy_items[idx] !== null && 'uom' in errors.pharmacy_items[idx])}
                                              />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.from_uom === value.from_uom}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 120, position: 'relative' }}>
                                          <TextField
                                            disabled={isPharmacyDisabled}
                                            sx={{
                                              ...(isPharmacyDisabled ? {
                                                backgroundColor: '#fafafa',
                                                opacity: 0.9,
                                                transition: 'background-color 0.3s ease, opacity 0.3s ease',
                                              } : {}),
                                              '& .MuiFormHelperText-root': {
                                                position: 'absolute',
                                                bottom: -18,
                                                left: 0,
                                                whiteSpace: 'nowrap',
                                                margin: 0,
                                                fontSize: '0.7rem',
                                              },
                                            }}
                                            fullWidth
                                            size="small"
                                            placeholder={tBilling('package_config.placeholders.enter', { defaultValue: 'Enter' })}
                                            type="number"
                                            name={`pharmacy_items[${idx}].discount_percent`}
                                            value={item.discount_percent}
                                            onChange={handleChange}
                                            inputProps={{ min: 0, max: 100 }}
                                            error={isPharmacyDiscountOutOfRange}
                                            helperText={pharmacyDiscountValidationMessage}
                                          />
                                        </TableCell>
                                        <TableCell align="center">
                                          <IconButton
                                            color="error"
                                            onClick={() => {
                                              handleDeleteClick(() => {
                                                if (idx === 0 && values.pharmacy_items && values.pharmacy_items.length === 1) {
                                                  setFieldValue('pharmacy_items[0].item', '');
                                                  setFieldValue('pharmacy_items[0].item_name', '');
                                                  setFieldValue('pharmacy_items[0].quantity', '');
                                                  setFieldValue('pharmacy_items[0].uom', '');
                                                  setFieldValue('pharmacy_items[0].discount_percent', '');
                                                } else {
                                                  remove(idx);
                                                }
                                              });
                                            }}
                                            sx={{ minWidth: 0, p: 0.5 }}
                                            disabled={isPharmacyDisabled}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        );
                      }}
                    </FieldArray>
                  </Box>
                )}

                {/* Summary Section */}
                <Box sx={{ my: 2 }}>
                  <Grid2 container spacing={2} alignItems="center">
                    <Grid2 size={{xl:3, lg:4, md:6, xs:12}}>
                      <TextField
                        label={tBilling('package_details.remark', { defaultValue: 'Remark' })}
                        fullWidth
                        disabled={values.package_definition.is_finalized}
                        sx={values.package_definition.is_finalized ? {
                          backgroundColor: '#fafafa',
                          opacity: 0.9,
                          transition: 'background-color 0.3s ease, opacity 0.3s ease',
                        } : undefined}
                        size="small"
                        name="package_definition.remark"
                        value={values.package_definition.remark || ''}
                        onChange={handleChange}
                        placeholder={tBilling('package_details.enter_remark', { defaultValue: 'Enter Remark' })}
                      />
                    </Grid2>
                  </Grid2>
                  {/* <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={<Field as={Checkbox} name="package_definition.is_finalized" checked={values.package_definition.is_finalized} color="primary" />}
                      label="Finalized"
                    />
                  </Box> */}
                </Box>


                {/* Validation Summary Section */}
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{tBilling('package_details.validations', { defaultValue: 'Validations' })}</Typography>
                  <Box sx={{ pl: 2 }}>
                    {validationMessages.map((message, index) => (
                      <Typography
                        key={`${message.text}-${index}`}
                        variant="body2"
                        sx={{
                          color: message.color,
                          fontWeight: message.weight,
                        }}
                      >
                        {`${index + 1}. ${message.text}`}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button data-testid="pkgdef-cancel-button" variant="outlined" color="inherit" sx={{ mr: 2, minWidth: 120, fontWeight: 600, borderRadius: 2 }} onClick={() => { 
                    if (onBackToStep1) {
                      onBackToStep1();
                    } else {
                      navigate('/ai/configuration/package-configuration/package', { state: { refetch: true, ts: Date.now() } });
                    }
                  }}>
                    {tBilling('package_config.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                  <Button
                    data-testid="pkgdef-save-finalize-button"
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={!allCustomValid}
                    sx={{ minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: '#444', '&:hover': { backgroundColor: '#222' } }}
                    onClick={async () => {
                      if (!allCustomValid) return;
                      // Validate form first
                      const validationErrors = await validateForm();
                      
                      // Create nested touched object to show all validation errors
                      const createTouchedObject = (obj: Record<string, unknown>): Record<string, unknown> => {
                        const touched: Record<string, unknown> = {};
                        for (const key in obj) {
                          if (Array.isArray(obj[key])) {
                            touched[key] = (obj[key] as unknown[]).map((item) => 
                              typeof item === 'object' && item !== null 
                                ? createTouchedObject(item as Record<string, unknown>) 
                                : true
                            );
                          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                            touched[key] = createTouchedObject(obj[key] as Record<string, unknown>);
                          } else {
                            touched[key] = true;
                          }
                        }
                        return touched;
                      };
                      
                      setTouched(createTouchedObject(values as unknown as Record<string, unknown>), false);
                      
                      if (Object.keys(validationErrors).length === 0) {
                        setOpenFinalizeModal(true);
                      }
                    }}
                  >
                    {tBilling('package_config.save_finalize', { defaultValue: 'Save & Finalize' })}
                  </Button>
                  <Button
                    data-testid="pkgdef-save-button"
                    type="button"
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120, fontWeight: 600, borderRadius: 2, backgroundColor: allCustomValid ? '#444' : '#9e9e9e', '&:hover': { backgroundColor: allCustomValid ? '#222' : '#9e9e9e' } }}
                    disabled={!allCustomValid}
                    onClick={() => {
                      if (!allCustomValid) return;
                      submitForm();
                    }}
                  >
                    {tBilling('package_config.save', { defaultValue: 'Save' })}
                  </Button>
                </Box>
                <ConfirmationModal
                  open={openModal}
                  handleClose={handleCloseModal}
                  onConfirm={() => handleConfirmedSubmit(values)}
                  type={'save'}
                  title={tBilling('package_config.package_details', { defaultValue: 'Package Details' })}
                  contentMessage={tBilling('package_config.messages.confirm_save_package_details', { defaultValue: 'Are you sure you want to save Package Details?' })}
                />
                <ConfirmationModal
                  open={openFinalizeModal}
                  handleClose={() => setOpenFinalizeModal(false)}
                  onConfirm={async () => {
                    await setFieldValue('package_definition.is_finalized', true);
                    setOpenFinalizeModal(false);
                    handleConfirmedSubmit({ ...values, package_definition: { ...values.package_definition, is_finalized: true } });
                  }}
                  type={'save'}
                  title={tBilling('package_config.finalize_package', { defaultValue: 'Finalize Package' })}
                  contentMessage={finalizeMessage}
                />
                <ConfirmationModal
                  open={deleteConfirmOpen}
                  handleClose={handleCancelDelete}
                  onConfirm={handleConfirmDelete}
                  type={'delete'}
                  title={tBilling('package_details.delete_confirmation', { defaultValue: 'Delete Confirmation' })}
                  contentMessage={tBilling('package_details.confirm_delete_item', { defaultValue: 'Are you sure you want to delete this item?' })}
                />
              </Form>
            </Box>
          );
        }}
      </Formik>
    </>
  );
};

// Main wrapper component that manages tabs for each tariff
const PackageDefinition: React.FC<PackageDefinitionProps> = ({ onBackToStep1, initialPackageData, packageId, onRefetchPackageData }) => {
  const { t: tBilling } = useTranslation('billing_config');
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [selectedCopyFromTariff, setSelectedCopyFromTariff] = React.useState<{ tariff_name: string; package_tariff_id: number } | null>(null);

  // Fetch package details without tariffId on component initialization
  const { data: initialPackageDetails, refetch: refetchInitialPackageDetails } = useAppQuery<packageDetailsForTariffResponse>(
    ['packageDefinitionDetailsInitial', packageId],
    () => getRequest<packageDetailsForTariffResponse>(`${packageDefinitionURL(packageId as number)}`),
    { 
      staleTime: 0, 
      gcTime: 0,
      enabled: typeof packageId === 'number'
    }
  );

  // Filter tariffs: if freeze is true, only show tariffs that have definitions
  const tariffs: PackageTariffItem[] = React.useMemo(() => {
    const allTariffs = initialPackageData?.tariffs ?? [];
    if (initialPackageData?.freeze && initialPackageDetails?.definitions) {
      const definedTariffNames = initialPackageDetails.definitions.map(d => d.tariff_name);
      // Check both tariff_name and name since tariff_name is optional
      return allTariffs.filter(t => {
        const tariffNameToCheck = t.tariff_name || t.name;
        return tariffNameToCheck && definedTariffNames.includes(tariffNameToCheck);
      });
    }
    return allTariffs;
  }, [initialPackageData?.tariffs, initialPackageData?.freeze, initialPackageDetails?.definitions]);

  // Get all tariff names for validation
  const allTariffNames = React.useMemo(() => {
    const allTariffs = initialPackageData?.tariffs ?? [];
    return allTariffs.map(t => t.tariff_name || t.name).filter(Boolean) as string[];
  }, [initialPackageData?.tariffs]);

  // Get defined tariff names from server
  const definedTariffNames = React.useMemo(() => {
    return (initialPackageDetails?.definitions || []).map(d => d.tariff_name);
  }, [initialPackageDetails?.definitions]);

  // Combined refetch function to refetch both package data and definitions
  const refetchAllData = React.useCallback(async () => {
    await onRefetchPackageData?.();
    await refetchInitialPackageDetails();
  }, [onRefetchPackageData, refetchInitialPackageDetails]);

  const activeTariff = tariffs[activeTabIndex];

  // Filter out the current tariff from options
  const tariffOptions = React.useMemo(() => {
    const existingTariffs = initialPackageDetails?.definitions || [];
    return existingTariffs.filter(t => t.tariff_name !== activeTariff?.tariff_name);
  }, [initialPackageDetails, activeTariff]);

  if (!tariffs || tariffs.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{tBilling('package_config.no_tariffs_selected', { defaultValue: 'No tariffs selected. Please go back to Step 1 and select at least one tariff.' })}</Typography>
        <Button onClick={onBackToStep1} variant="contained" sx={{ mt: 2 }}>
          {tBilling('package_config.back_to_step_1', { defaultValue: 'Back to Step 1' })}
        </Button>
      </Box>
    );
  }
  const handleStepChange = (index: number) => {
    setActiveTabIndex(index);
    setSelectedCopyFromTariff(null); 
  };

  return (
    <Box>
      {/* Tariff Tabs */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>{tBilling('package_config.tariff_type', { defaultValue: 'Tariff Type' })}</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#fafafa',
              borderRadius: '10px',
              p: '2px',
              width: 'fit-content',
              boxShadow: 'inset 0 0 4px rgba(0,0,0,0.05)',
            }}
          >
            {tariffs.map((tariff: PackageTariffItem, index: number) => (
              <Box
                key={tariff.id}
                onClick={() => { handleStepChange(index);}}
                sx={{
                  cursor: 'pointer',
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  backgroundColor: activeTabIndex === index ? '#fff' : 'transparent',
                  boxShadow:
                    activeTabIndex === index ? '0px 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.3s ease',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: activeTabIndex === index ? '#e07b53' : '#232323',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tariff.tariff_name || `${tBilling('package_config.tariff', { defaultValue: 'Tariff' })} ${index + 1}`}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Select tariff type for package Autocomplete */}
          {tariffOptions.length > 0 && !initialPackageData?.freeze && (
            <Autocomplete
              size="small"
              sx={{ minWidth: 250 }}
              options={tariffOptions}
              getOptionLabel={(option) => option.tariff_name}
              value={selectedCopyFromTariff}
              onChange={(_, newValue) => setSelectedCopyFromTariff(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tBilling('package_config.select_tariff_type_for_package', { defaultValue: 'Select tariff type for package' })}
                  placeholder={tBilling('package_config.select_tariff', { defaultValue: 'Select tariff' })}
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.package_tariff_id === value.package_tariff_id}
            />
          )}
        </Box>
      </Box>

      {/* Tab Content - Form for each tariff */}
      {tariffs.map((tariff: PackageTariffItem, index: number) => (
        <div 
          key={tariff.id} 
          role="tabpanel" 
          hidden={activeTabIndex !== index}
          style={{ width: '100%' }}
        >
          {activeTabIndex === index && packageId && (
            <PackageDefinitionForm
              packageId={packageId}
              tariffId={tariff.id}
              tariffName={tariff.tariff_name || `${tBilling('package_config.tariff', { defaultValue: 'Tariff' })} ${index + 1}`}
              onBackToStep1={onBackToStep1}
              packageDetailsValues={initialPackageData}
              import_tariff_id={selectedCopyFromTariff?.package_tariff_id}
              onRefetchInitialDetails={refetchAllData}
              allTariffNames={allTariffNames}
              definedTariffNames={definedTariffNames}
            />
          )}
        </div>
      ))}
    </Box>
  );
};

export default PackageDefinition;
